// @ts-nocheck
//
// Order Tracking local cache (localStorage-backed).
//
// Stores the RAW server rows (same shape getOrderTracking returns) so the page
// can reuse its existing mapApiRecordToOpenOrder() on read and buildCreatePayload()
// on write — no duplicated mapping logic here.
//
// Everything goes through the small safeGet/safeSet helpers, so if the data ever
// outgrows localStorage you can swap the internals for IndexedDB without touching
// the rest of the app.

import { getOrderTracking, updateOrderTracking } from "./API";

const RECORDS_KEY = "ot_records";
const META_KEY = "ot_meta";
const DIRTY_KEY = "ot_dirty";

// ---- low-level storage helpers (swappable) --------------------------------

const safeGet = (key: string, fallback: any) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const safeSet = (key: string, value: any): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e: any) {
    // QuotaExceededError or serialization failure — don't crash the app.
    console.warn("[orderTrackingCache] write failed:", e?.name || e);
    return false;
  }
};

// ---- helpers --------------------------------------------------------------

export const getRowId = (row: any) =>
  row?.id ?? row?.ot_id ?? row?.order_tracking_id ?? undefined;

export const extractOrderTrackingRows = (response: any): any[] =>
  Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

// ---- records --------------------------------------------------------------

export const otReadRecords = (): any[] => safeGet(RECORDS_KEY, []);

export const otWriteRecords = (rows: any[]): boolean =>
  safeSet(RECORDS_KEY, Array.isArray(rows) ? rows : []);

export const otCacheHasData = (): boolean => otReadRecords().length > 0;

export const otClearAll = () => {
  try {
    localStorage.removeItem(RECORDS_KEY);
    localStorage.removeItem(META_KEY);
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    /* ignore */
  }
};

// ---- meta -----------------------------------------------------------------

export const otGetMeta = () => safeGet(META_KEY, { lastSyncedAt: 0 });
export const otSetMeta = (meta: any) =>
  safeSet(META_KEY, { ...otGetMeta(), ...meta });

// ---- dirty queue (records whose DB save is pending / failed) --------------

// Stored as { [id]: patch } — only the fields that are still unsaved. Keeping
// just the changed fields (not the whole cached row) means a retry can never
// write a stale copy of a field another page updated since, e.g. serials
// synced from Open Order. The old format (array of ids) held no patch, so it
// is dropped rather than replayed as a full-row overwrite.
const otGetDirtyPatches = (): Record<string, any> => {
  const stored = safeGet(DIRTY_KEY, {});
  return stored && typeof stored === "object" && !Array.isArray(stored)
    ? stored
    : {};
};

export const otGetDirty = (): string[] => Object.keys(otGetDirtyPatches());

export const otMarkDirty = (id: any, patch: any = {}) => {
  if (id == null) return;
  const patches = otGetDirtyPatches();
  const key = String(id);
  patches[key] = { ...(patches[key] || {}), ...(patch || {}) };
  safeSet(DIRTY_KEY, patches);
};

export const otClearDirty = (id: any) => {
  const patches = otGetDirtyPatches();
  delete patches[String(id)];
  safeSet(DIRTY_KEY, patches);
};

// ---- merge (the "compare, otherwise add" logic) ---------------------------
//
// Server is the source of truth, EXCEPT fields with local unsynced edits (in
// the dirty queue) — those are laid over the server row so we don't clobber a
// pending change. Cached-only records that aren't dirty are treated as deleted
// on the server and dropped.

export const otMergeServerRows = (serverRows: any[]): any[] => {
  const patches = otGetDirtyPatches();
  const dirty = new Set(Object.keys(patches));
  const cached = otReadRecords();
  const cachedById = new Map<string, any>();
  cached.forEach((r) => {
    const id = getRowId(r);
    if (id != null) cachedById.set(String(id), r);
  });

  const merged: any[] = [];
  const seen = new Set<string>();

  // Walk server rows; dirty ones get their pending fields laid on top.
  (serverRows || []).forEach((srv) => {
    const id = getRowId(srv);
    const key = id != null ? String(id) : null;
    if (key && dirty.has(key)) {
      merged.push({ ...srv, ...patches[key] });
    } else {
      merged.push(srv);
    }
    if (key) seen.add(key);
  });

  // Keep cached-only rows that are still dirty (e.g. created/edited offline).
  cached.forEach((r) => {
    const id = getRowId(r);
    const key = id != null ? String(id) : null;
    if (key && dirty.has(key) && !seen.has(key)) merged.push(r);
  });

  return merged;
};

// ---- single-record upsert (called on edit) --------------------------------
//
// payload is the server-shaped object from buildCreatePayload(). We merge it
// into the existing cached row (preserving id and any server-only fields).

export const otUpsertRecord = (id: any, payload: any) => {
  if (id == null) return;
  const rows = otReadRecords();
  const idx = rows.findIndex((r) => String(getRowId(r)) === String(id));
  if (idx >= 0) {
    rows[idx] = { ...rows[idx], ...payload };
  } else {
    rows.push({ id, ...payload });
  }
  otWriteRecords(rows);
};

export const otRemoveRecord = (id: any) => {
  if (id == null) return;
  otWriteRecords(
    otReadRecords().filter((r) => String(getRowId(r)) !== String(id)),
  );
  otClearDirty(id);
};

// ---- seed / sync ----------------------------------------------------------

// Called once after login: fetch everything and seed/merge the cache.
export const seedOrderTrackingCache = async (): Promise<void> => {
  try {
    const response = await getOrderTracking({ page: 1, limit: 10000 });
    const rows = extractOrderTrackingRows(response);
    otWriteRecords(otCacheHasData() ? otMergeServerRows(rows) : rows);
    otSetMeta({ lastSyncedAt: Date.now() });
  } catch (e) {
    // Network/login race — the page will fetch on its own when opened.
    console.warn("[orderTrackingCache] seed failed:", e);
  }
};

// Re-send any records whose silent save failed. Safe to call repeatedly
// (on app start, on login, and when the browser comes back online).
export const flushOrderTrackingDirty = async (): Promise<void> => {
  const patches = otGetDirtyPatches();
  for (const [id, patch] of Object.entries(patches)) {
    const {
      id: _id,
      ot_id: _otId,
      order_tracking_id: _otrId,
      ...payload
    } = patch || {};
    if (!Object.keys(payload).length) {
      otClearDirty(id);
      continue;
    }
    try {
      await updateOrderTracking(id, payload);
      otClearDirty(id);
    } catch {
      // Still failing — keep it dirty and try again next time.
    }
  }
};
